import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search, Edit2, Trash2, Eye, Calendar, RefreshCw, 
  ChevronDown, ChevronRight, Users, Building2, 
  Shield, UserCog, ArrowRightLeft, Mail, Phone, Crown,
  Download, Key, ToggleLeft, ToggleRight, AlertTriangle,
  CheckCircle, XCircle, Clock, Gift, User, Hash, Star,
  X, Info, AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import API from '../../api';

// ==================== TOAST COMPONENT ====================
const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div style={toastStyles.container}>
      {toasts.map(toast => (
        <Toast key={toast.id} {...toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  );
};

const Toast = ({ id, type, title, message, onClose }) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(onClose, 300);
    }, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(onClose, 300);
  };

  const icons = {
    success: <CheckCircle size={20} />,
    error: <XCircle size={20} />,
    warning: <AlertTriangle size={20} />,
    info: <Info size={20} />
  };

  const colors = {
    success: { bg: 'linear-gradient(135deg, #10b981, #059669)', border: '#10b981', icon: '#10b981' },
    error: { bg: 'linear-gradient(135deg, #ef4444, #dc2626)', border: '#ef4444', icon: '#ef4444' },
    warning: { bg: 'linear-gradient(135deg, #f59e0b, #d97706)', border: '#f59e0b', icon: '#f59e0b' },
    info: { bg: 'linear-gradient(135deg, #6366f1, #4f46e5)', border: '#6366f1', icon: '#6366f1' }
  };

  return (
    <div style={{
      ...toastStyles.toast,
      animation: isExiting ? 'slideOut 0.3s ease forwards' : 'slideIn 0.3s ease',
      borderLeftColor: colors[type].border
    }}>
      <div style={{ ...toastStyles.iconWrapper, background: colors[type].bg }}>
        {icons[type]}
      </div>
      <div style={toastStyles.content}>
        <div style={toastStyles.title}>{title}</div>
        {message && <div style={toastStyles.message}>{message}</div>}
      </div>
      <button onClick={handleClose} style={toastStyles.closeBtn}>
        <X size={16} />
      </button>
      <div style={toastStyles.progressBar}>
        <div style={{ ...toastStyles.progress, background: colors[type].border }} />
      </div>
    </div>
  );
};

const toastStyles = {
  container: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxWidth: '400px',
    width: '100%',
    pointerEvents: 'none'
  },
  toast: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.15), 0 4px 12px rgba(0,0,0,0.1)',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '16px',
    borderLeft: '4px solid',
    position: 'relative',
    overflow: 'hidden',
    pointerEvents: 'auto'
  },
  iconWrapper: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    flexShrink: 0
  },
  content: {
    flex: 1,
    minWidth: 0
  },
  title: {
    fontWeight: '600',
    color: '#1e293b',
    fontSize: '14px',
    marginBottom: '2px'
  },
  message: {
    color: '#64748b',
    fontSize: '13px',
    lineHeight: '1.4'
  },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    color: '#94a3b8',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s'
  },
  progressBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: '#f1f5f9'
  },
  progress: {
    height: '100%',
    animation: 'progressShrink 4s linear forwards'
  }
};

// ==================== MAIN COMPONENT ====================
export default function AdminUsersPage() {
  // ==================== STATE ====================
  const [superAdmins, setSuperAdmins] = useState([]);
  const [adminsData, setAdminsData] = useState([]);
  const [orphanStaff, setOrphanStaff] = useState([]);
  const [stats, setStats] = useState({ 
    totalSuperAdmins: 0,
    totalAdmins: 0, 
    totalStaff: 0, 
    activeAdmins: 0, 
    trialAdmins: 0,
    expiredAdmins: 0
  });
  
  const [expandedAdmins, setExpandedAdmins] = useState({});
  const [showSuperAdmins, setShowSuperAdmins] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // View/Edit/Delete Modal
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserType, setSelectedUserType] = useState('admin');
  
  // Trial Modal
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [trialDate, setTrialDate] = useState('');
  const [trialUser, setTrialUser] = useState(null);
  
  // Transfer Modal
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferStaff, setTransferStaff] = useState(null);
  const [transferToAdmin, setTransferToAdmin] = useState('');

  // Role Change Modal
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleUser, setRoleUser] = useState(null);

  // Password Reset Modal
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // Toast State
  const [toasts, setToasts] = useState([]);

  const navigate = useNavigate();
  const currentUserId = localStorage.getItem('userId');

  // ==================== TOAST FUNCTIONS ====================
  const showToast = useCallback((type, title, message = '') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, type, title, message }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // ==================== FETCH DATA ====================
  useEffect(() => {
    fetchUsersHierarchy();
  }, []);

  const fetchUsersHierarchy = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await API.get('/users/admin/users-hierarchy', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuperAdmins(data.superAdmins || []);
      setAdminsData(data.admins || []);
      setOrphanStaff(data.orphanStaff || []);
      setStats(data.stats || {});
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error.response?.status === 403) {
        showToast('error', 'Access Denied', 'SuperAdmin access required');
        navigate('/login');
      } else {
        showToast('error', 'Error', 'Failed to fetch users data');
      }
    }
    setLoading(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchUsersHierarchy();
    showToast('success', 'Refreshed', 'Data updated successfully');
    setRefreshing(false);
  };

  // ==================== UI HELPERS ====================
  const toggleAdmin = (id) => setExpandedAdmins(prev => ({ ...prev, [id]: !prev[id] }));
  
  const expandAll = () => { 
    const exp = {}; 
    adminsData.forEach(a => exp[a._id] = true); 
    setExpandedAdmins(exp);
    showToast('info', 'Expanded All', 'All admin cards expanded');
  };
  
  const collapseAll = () => {
    setExpandedAdmins({});
    showToast('info', 'Collapsed All', 'All admin cards collapsed');
  };

  // Filter SuperAdmins
  const filteredSuperAdmins = superAdmins.filter(sa => {
    const s = searchTerm.toLowerCase();
    const matchSearch = 
      sa.name?.toLowerCase().includes(s) || 
      sa.email?.toLowerCase().includes(s) || 
      sa.phone?.toLowerCase().includes(s);
    
    const matchRole = filterRole === 'all' || filterRole === 'superadmin';
    return matchSearch && matchRole;
  });

  // Filter Admins
  const filteredAdmins = adminsData.filter(admin => {
    const s = searchTerm.toLowerCase();
    const matchSearch = 
      admin.name?.toLowerCase().includes(s) || 
      admin.email?.toLowerCase().includes(s) || 
      admin.pharmacyName?.toLowerCase().includes(s) ||
      admin.phone?.toLowerCase().includes(s) ||
      admin.pan?.toLowerCase().includes(s) ||
      admin.staffMembers?.some(st => 
        st.name?.toLowerCase().includes(s) || 
        st.email?.toLowerCase().includes(s)
      );
    
    const matchStatus = filterStatus === 'all' || admin.status === filterStatus;
    const matchRole = filterRole === 'all' || filterRole === 'admin';
    
    return matchSearch && matchStatus && matchRole;
  });

  const getStatusBadge = (status) => {
    const styles = {
      active: { bg: '#d1fae5', color: '#065f46', icon: <CheckCircle size={12} /> },
      inactive: { bg: '#fef3c7', color: '#92400e', icon: <XCircle size={12} /> },
      trial: { bg: '#dbeafe', color: '#1e40af', icon: <Gift size={12} /> },
      expired: { bg: '#fee2e2', color: '#991b1b', icon: <Clock size={12} /> },
      blocked: { bg: '#fecaca', color: '#dc2626', icon: <AlertTriangle size={12} /> },
      suspended: { bg: '#fecaca', color: '#dc2626', icon: <AlertTriangle size={12} /> }
    };
    return styles[status] || styles.inactive;
  };

  const getRoleBadge = (role) => {
    const styles = {
      superadmin: { bg: '#fef3c7', color: '#b45309', icon: <Crown size={12} /> },
      admin: { bg: '#f3e8ff', color: '#7e22ce', icon: <Shield size={12} /> },
      staff: { bg: '#dbeafe', color: '#1d4ed8', icon: <User size={12} /> }
    };
    return styles[role] || styles.admin;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getTrialInfo = (user) => {
    if (user.status === 'active') return null;
    if (!user.trial?.endDate) return null;
    
    const endDate = new Date(user.trial.endDate);
    const now = new Date();
    const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) {
      return { text: 'Expired', color: '#ef4444', days: Math.abs(daysLeft) + ' days ago' };
    } else if (daysLeft <= 3) {
      return { text: `${daysLeft} days left`, color: '#f59e0b', days: 'Expiring soon' };
    } else {
      return { text: `${daysLeft} days left`, color: '#10b981', days: formatDate(endDate) };
    }
  };

  // ==================== ACTION HANDLERS ====================
  
  const handleView = (user, type) => {
    setSelectedUser(user);
    setSelectedUserType(type);
    setModalMode('view');
    setShowModal(true);
  };

  const handleEdit = (user, type) => {
    setSelectedUser({ ...user });
    setSelectedUserType(type);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDelete = (user, type) => {
    setSelectedUser(user);
    setSelectedUserType(type);
    setModalMode('delete');
    setShowModal(true);
  };

  const handleRoleClick = (user) => {
    setRoleUser(user);
    setShowRoleModal(true);
  };

  const handleSetTrial = (user) => {
    setTrialUser(user);
    const date = user.trial?.endDate 
      ? new Date(user.trial.endDate).toISOString().split('T')[0] 
      : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    setTrialDate(date);
    setShowTrialModal(true);
  };

  const handleTransfer = (staff) => {
    setTransferStaff(staff);
    setTransferToAdmin('');
    setShowTransferModal(true);
  };

  const handleResetPassword = (user) => {
    setPasswordUser(user);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const handleToggleActive = async (user, type) => {
    try {
      const token = localStorage.getItem('token');
      
      if (type === 'staff') {
        await API.put(`/staff/${user._id}/toggle-status`, {}, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        const newUserStatus = user.status === 'active' ? 'inactive' : 'active';
        await API.put(`/users/admin/users/${user._id}`, { status: newUserStatus }, { headers: { Authorization: `Bearer ${token}` } });
      }
      
      fetchUsersHierarchy();
      showToast('success', 'Status Updated', `User is now ${user.status === 'active' ? 'inactive' : 'active'}`);
    } catch (e) {
      showToast('error', 'Error', e.response?.data?.message || 'Failed to update status');
    }
  };

  // ==================== API ACTIONS ====================

  const confirmRoleChange = async () => {
    try {
      const newRole = roleUser.role === 'admin' ? 'superadmin' : 'admin';
      const token = localStorage.getItem('token');
      
      await API.put(`/users/admin/users/${roleUser._id}`, 
        { role: newRole }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowRoleModal(false);
      fetchUsersHierarchy();
      showToast('success', 'Role Changed', `User is now ${newRole.toUpperCase()}`);
    } catch (e) {
      showToast('error', 'Error', e.response?.data?.message || 'Failed to change role');
    }
  };

  const saveTrial = async () => {
    try {
      const token = localStorage.getItem('token');
      await API.post('/users/admin/set-trial', 
        { userId: trialUser._id, trialEndDate: trialDate }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowTrialModal(false);
      fetchUsersHierarchy();
      showToast('success', 'Trial Updated', `Trial extended to ${formatDate(trialDate)}`);
    } catch (e) {
      showToast('error', 'Error', e.response?.data?.message || 'Failed to update trial');
    }
  };

  const resetTrial = async () => {
    try {
      const token = localStorage.getItem('token');
      await API.post('/users/admin/set-trial', 
        { userId: trialUser._id, resetTrial: true }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowTrialModal(false);
      fetchUsersHierarchy();
      showToast('success', 'Trial Reset', 'Trial reset to 7 days from today');
    } catch (e) {
      showToast('error', 'Error', e.response?.data?.message || 'Failed to reset trial');
    }
  };

  const confirmTransfer = async () => {
    if (!transferToAdmin) {
      showToast('warning', 'Selection Required', 'Please select a target admin');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await API.post(`/staff/${transferStaff._id}/transfer`, 
        { newAdminId: transferToAdmin }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowTransferModal(false);
      fetchUsersHierarchy();
      showToast('success', 'Staff Transferred', `${transferStaff.name} has been transferred`);
    } catch (e) {
      showToast('error', 'Transfer Failed', e.response?.data?.message || 'Failed to transfer staff');
    }
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = selectedUserType === 'staff' 
        ? `/staff/${selectedUser._id}` 
        : `/users/admin/users/${selectedUser._id}`;
      
      await API.delete(url, { headers: { Authorization: `Bearer ${token}` } });
      setShowModal(false);
      fetchUsersHierarchy();
      showToast('success', 'User Deleted', `${selectedUser.name} has been removed`);
    } catch (e) {
      showToast('error', 'Delete Failed', e.response?.data?.message || 'Failed to delete user');
    }
  };

  const saveEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = selectedUserType === 'staff' 
        ? `/staff/${selectedUser._id}` 
        : `/users/admin/users/${selectedUser._id}`;
      
      await API.put(url, selectedUser, { headers: { Authorization: `Bearer ${token}` } });
      setShowModal(false);
      fetchUsersHierarchy();
      showToast('success', 'Changes Saved', `${selectedUser.name}'s info updated`);
    } catch (e) {
      showToast('error', 'Save Failed', e.response?.data?.message || 'Failed to save changes');
    }
  };

  const confirmResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      showToast('warning', 'Invalid Password', 'Password must be at least 6 characters');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await API.put(`/staff/${passwordUser._id}/reset-password`, 
        { newPassword }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowPasswordModal(false);
      showToast('success', 'Password Reset', `New password set for ${passwordUser.name}`);
    } catch (e) {
      showToast('error', 'Reset Failed', e.response?.data?.message || 'Failed to reset password');
    }
  };

  const exportToCSV = () => {
    const headers = ['Type', 'Name', 'Email', 'Phone', 'Pharmacy', 'PAN', 'Role', 'Status', 'Created At'];
    const rows = [];
    
    // Add SuperAdmins
    filteredSuperAdmins.forEach(sa => {
      rows.push([
        'SuperAdmin',
        sa.name,
        sa.email,
        sa.phone || '',
        sa.pharmacyName || '',
        sa.pan || '',
        'superadmin',
        sa.status,
        formatDate(sa.createdAt)
      ]);
    });
    
    // Add Admins and Staff
    filteredAdmins.forEach(admin => {
      rows.push([
        'Admin',
        admin.name,
        admin.email,
        admin.phone,
        admin.pharmacyName,
        admin.pan,
        'admin',
        admin.status,
        formatDate(admin.createdAt)
      ]);
      
      admin.staffMembers?.forEach(staff => {
        rows.push([
          'Staff',
          staff.name,
          staff.email,
          staff.phone,
          admin.pharmacyName,
          '-',
          'staff',
          staff.isActive ? 'active' : 'inactive',
          formatDate(staff.createdAt)
        ]);
      });
    });
    
    const csvContent = [headers, ...rows].map(row => 
      row.map(cell => `"${cell || ''}"`).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    showToast('success', 'Export Complete', `${rows.length} users exported to CSV`);
  };

  // ==================== RENDER ====================
  return (
    <div style={styles.container}>
      
      {/* TOAST CONTAINER */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>
            <Shield size={28} color="#6366f1" />
            User Hierarchy Management
          </h1>
          <p style={styles.subtitle}>Manage SuperAdmins, Admins, and their Staff members</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={exportToCSV} style={styles.exportBtn}>
            <Download size={18} /> Export CSV
          </button>
        </div>
      </div>

      {/* STATS CARDS */}
      <div style={styles.statsGrid}>
        <StatCard title="Super Admins" value={stats.totalSuperAdmins} color="#f59e0b" icon={<Crown size={24} />} />
        <StatCard title="Admins" value={stats.totalAdmins} color="#6366f1" icon={<Building2 size={24} />} />
        <StatCard title="Total Staff" value={stats.totalStaff} color="#10b981" icon={<Users size={24} />} />
        <StatCard title="Active" value={stats.activeAdmins} color="#22c55e" icon={<CheckCircle size={24} />} />
        <StatCard title="Trial" value={stats.trialAdmins} color="#3b82f6" icon={<Gift size={24} />} />
        <StatCard title="Expired" value={stats.expiredAdmins} color="#ef4444" icon={<Clock size={24} />} />
      </div>

      {/* CONTROLS */}
      <div style={styles.controls}>
        <div style={styles.searchWrapper}>
          <Search size={18} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by name, email, phone, pharmacy, PAN..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>
        
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.select}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="trial">Trial</option>
          <option value="expired">Expired</option>
          <option value="inactive">Inactive</option>
          <option value="blocked">Blocked</option>
        </select>
        
        <select value={filterRole} onChange={e => setFilterRole(e.target.value)} style={styles.select}>
          <option value="all">All Roles</option>
          <option value="superadmin">SuperAdmin</option>
          <option value="admin">Admin</option>
        </select>
        
        <button onClick={expandAll} style={styles.controlBtn}><ChevronDown size={16} /> Expand All</button>
        <button onClick={collapseAll} style={styles.controlBtn}><ChevronRight size={16} /> Collapse</button>
        <button onClick={handleRefresh} disabled={refreshing} style={{ ...styles.controlBtn, background: '#6366f1', color: 'white' }}>
          <RefreshCw size={16} className={refreshing ? 'spin' : ''} /> {refreshing ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* ==================== SUPER ADMINS SECTION ==================== */}
      {(filterRole === 'all' || filterRole === 'superadmin') && filteredSuperAdmins.length > 0 && (
        <div style={styles.sectionContainer}>
          <div 
            style={styles.sectionHeader}
            onClick={() => setShowSuperAdmins(!showSuperAdmins)}
          >
            <div style={styles.sectionTitle}>
              <div style={styles.sectionIcon}>
                <Crown size={20} color="#f59e0b" />
              </div>
              <span>Super Admins ({filteredSuperAdmins.length})</span>
            </div>
            <div style={styles.sectionToggle}>
              {showSuperAdmins ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </div>
          </div>
          
          {showSuperAdmins && (
            <div style={styles.sectionBody}>
              {filteredSuperAdmins.map(superAdmin => (
                <div key={superAdmin._id} style={styles.superAdminRow}>
                  <div style={styles.superAdminAvatar}>
                    <Crown size={20} color="#f59e0b" />
                  </div>
                  
                  <div style={styles.userInfo}>
                    <div style={styles.userName}>
                      {superAdmin.name}
                      <span style={{ ...styles.badge, background: '#fef3c7', color: '#b45309' }}>
                        <Crown size={10} /> SUPER ADMIN
                      </span>
                      <span style={{ ...styles.badge, background: getStatusBadge(superAdmin.status).bg, color: getStatusBadge(superAdmin.status).color }}>
                        {getStatusBadge(superAdmin.status).icon} {superAdmin.status?.toUpperCase()}
                      </span>
                    </div>
                    <div style={styles.userMeta}>
                      <span><Mail size={12} /> {superAdmin.email}</span>
                      <span><Phone size={12} /> {superAdmin.phone || 'N/A'}</span>
                      {superAdmin.pharmacyName && <span><Building2 size={12} /> {superAdmin.pharmacyName}</span>}
                      <span><Clock size={12} /> Joined: {formatDate(superAdmin.createdAt)}</span>
                    </div>
                  </div>
                  
                  <div style={styles.actions}>
                    <ActionBtn title="View Details" icon={<Eye size={16} />} color="#6366f1" onClick={() => handleView(superAdmin, 'superadmin')} />
                    <ActionBtn 
                      title="Demote to Admin" 
                      icon={<Shield size={16} />} 
                      color="#8b5cf6" 
                      onClick={() => handleRoleClick(superAdmin)} 
                    />
                    <ActionBtn title="Edit" icon={<Edit2 size={16} />} color="#10b981" onClick={() => handleEdit(superAdmin, 'superadmin')} />
                    <ActionBtn title="Delete" icon={<Trash2 size={16} />} color="#ef4444" onClick={() => handleDelete(superAdmin, 'superadmin')} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Current User Info */}
      <div style={styles.currentUserBanner}>
        <Star size={16} color="#6366f1" />
        <span>You are logged in as <strong>Super Admin</strong> (Your account is not shown above for security)</span>
      </div>

      {/* ==================== ADMINS SECTION ==================== */}
      {(filterRole === 'all' || filterRole === 'admin') && (
        <div style={styles.sectionContainer}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionTitle}>
              <div style={{ ...styles.sectionIcon, background: '#f3e8ff' }}>
                <Shield size={20} color="#7e22ce" />
              </div>
              <span>Admins & Staff ({filteredAdmins.length} admins, {filteredAdmins.reduce((sum, a) => sum + (a.staffMembers?.length || 0), 0)} staff)</span>
            </div>
          </div>
          
          <div style={styles.userList}>
            {loading ? (
              <div style={styles.loadingState}>
                <div style={styles.spinner}></div>
                <p>Loading users...</p>
              </div>
            ) : filteredAdmins.length === 0 ? (
              <div style={styles.emptyState}>
                <Users size={48} color="#cbd5e1" />
                <p>No admins found matching your criteria</p>
              </div>
            ) : (
              filteredAdmins.map(admin => (
                <div key={admin._id} style={styles.adminCard}>
                  
                  {/* ADMIN ROW */}
                  <div style={{ ...styles.adminRow, background: expandedAdmins[admin._id] ? '#f8fafc' : 'white' }}>
                    
                    <button
                      onClick={() => toggleAdmin(admin._id)}
                      disabled={!admin.staffMembers?.length}
                      style={styles.expandBtn}
                    >
                      {expandedAdmins[admin._id] 
                        ? <ChevronDown size={20} color="#64748b" /> 
                        : <ChevronRight size={20} color={admin.staffMembers?.length ? '#64748b' : '#e2e8f0'} />
                      }
                    </button>

                    <div style={styles.avatar}>
                      {admin.name?.charAt(0).toUpperCase()}
                    </div>

                    <div style={styles.userInfo}>
                      <div style={styles.userName}>
                        {admin.name}
                        <span style={{ ...styles.badge, background: '#f3e8ff', color: '#7e22ce' }}>
                          <Shield size={10} /> ADMIN
                        </span>
                        <span style={{ ...styles.badge, background: getStatusBadge(admin.status).bg, color: getStatusBadge(admin.status).color }}>
                          {getStatusBadge(admin.status).icon} {admin.status.toUpperCase()}
                        </span>
                        {getTrialInfo(admin) && (
                          <span style={{ ...styles.badge, background: '#fef3c7', color: getTrialInfo(admin).color }}>
                            <Clock size={10} /> {getTrialInfo(admin).text}
                          </span>
                        )}
                      </div>
                      
                      <div style={styles.userMeta}>
                        <span><Building2 size={12} /> {admin.pharmacyName || 'N/A'}</span>
                        <span><Mail size={12} /> {admin.email}</span>
                        <span><Phone size={12} /> {admin.phone || 'N/A'}</span>
                        <span><Hash size={12} /> {admin.pan || 'N/A'}</span>
                      </div>
                    </div>

                    <div style={{ ...styles.staffCount, color: admin.staffMembers?.length ? '#10b981' : '#cbd5e1' }}>
                      <Users size={16} />
                      {admin.staffMembers?.length || 0} Staff
                    </div>

                    <div style={styles.actions}>
                      <ActionBtn title="View Details" icon={<Eye size={16} />} color="#6366f1" onClick={() => handleView(admin, 'admin')} />
                      <ActionBtn 
                        title="Promote to SuperAdmin" 
                        icon={<Crown size={16} />} 
                        color="#f59e0b" 
                        onClick={() => handleRoleClick(admin)} 
                      />
                      <ActionBtn title="Edit" icon={<Edit2 size={16} />} color="#10b981" onClick={() => handleEdit(admin, 'admin')} />
                      <ActionBtn title="Set Trial" icon={<Calendar size={16} />} color="#8b5cf6" onClick={() => handleSetTrial(admin)} />
                      <ActionBtn 
                        title="Delete" 
                        icon={<Trash2 size={16} />} 
                        color="#ef4444" 
                        onClick={() => handleDelete(admin, 'admin')} 
                        disabled={admin.staffMembers?.length > 0}
                      />
                    </div>
                  </div>

                  {/* STAFF ROWS */}
                  {expandedAdmins[admin._id] && admin.staffMembers?.length > 0 && (
                    <div style={styles.staffContainer}>
                      {admin.staffMembers.map(staff => (
                        <div key={staff._id} style={styles.staffRow}>
                          <div style={styles.staffConnector}></div>
                          
                          <div style={styles.staffAvatar}>
                            {staff.name?.charAt(0).toUpperCase()}
                          </div>
                          
                          <div style={styles.staffInfo}>
                            <div style={styles.staffName}>
                              {staff.name}
                              <span style={{ ...styles.badge, background: '#dbeafe', color: '#1d4ed8' }}>
                                <User size={10} /> STAFF
                              </span>
                              <span style={{ ...styles.badge, background: '#f1f5f9', color: '#64748b' }}>
                                ID: {staff.staffId}
                              </span>
                              {!staff.isActive && (
                                <span style={{ ...styles.badge, background: '#fee2e2', color: '#dc2626' }}>
                                  <XCircle size={10} /> INACTIVE
                                </span>
                              )}
                            </div>
                            <div style={styles.staffMeta}>
                              <span><Mail size={12} /> {staff.email}</span>
                              <span><Phone size={12} /> {staff.phone || 'N/A'}</span>
                              <span><Key size={12} /> {staff.permissions?.length || 0} permissions</span>
                            </div>
                          </div>
                          
                          <div style={styles.actions}>
                            <ActionBtn title="View" icon={<Eye size={14} />} color="#6366f1" onClick={() => handleView(staff, 'staff')} />
                            <ActionBtn title="Edit" icon={<Edit2 size={14} />} color="#10b981" onClick={() => handleEdit(staff, 'staff')} />
                            <ActionBtn 
                              title={staff.isActive ? "Deactivate" : "Activate"} 
                              icon={staff.isActive ? <ToggleRight size={14} /> : <ToggleLeft size={14} />} 
                              color={staff.isActive ? "#22c55e" : "#94a3b8"} 
                              onClick={() => handleToggleActive(staff, 'staff')} 
                            />
                            <ActionBtn title="Reset Password" icon={<Key size={14} />} color="#8b5cf6" onClick={() => handleResetPassword(staff)} />
                            <ActionBtn title="Transfer" icon={<ArrowRightLeft size={14} />} color="#f59e0b" onClick={() => handleTransfer(staff)} />
                            <ActionBtn title="Delete" icon={<Trash2 size={14} />} color="#ef4444" onClick={() => handleDelete(staff, 'staff')} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {expandedAdmins[admin._id] && (!admin.staffMembers || admin.staffMembers.length === 0) && (
                    <div style={styles.noStaff}>
                      <Users size={16} /> No staff members
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Summary */}
      <div style={styles.summary}>
        Showing {filteredSuperAdmins.length} super admins, {filteredAdmins.length} admins, {filteredAdmins.reduce((sum, a) => sum + (a.staffMembers?.length || 0), 0)} staff members
      </div>

      {/* ==================== MODALS ==================== */}

      {/* Role Change Modal */}
      {showRoleModal && roleUser && (
        <Modal onClose={() => setShowRoleModal(false)}>
          <div style={styles.modalHeader}>
            <Crown size={24} color="#f59e0b" />
            <h3>Change User Role</h3>
          </div>
          
          <div style={styles.modalBody}>
            <div style={styles.infoBox}>
              <p><strong>User:</strong> {roleUser.name}</p>
              <p><strong>Email:</strong> {roleUser.email}</p>
              <p><strong>Current Role:</strong> 
                <span style={{ ...styles.badge, marginLeft: '8px', background: getRoleBadge(roleUser.role).bg, color: getRoleBadge(roleUser.role).color }}>
                  {roleUser.role.toUpperCase()}
                </span>
              </p>
            </div>
            
            <div style={{ ...styles.warningBox, background: roleUser.role === 'admin' ? '#fffbeb' : '#fef2f2', borderColor: roleUser.role === 'admin' ? '#fef3c7' : '#fecaca' }}>
              <AlertTriangle size={20} color={roleUser.role === 'admin' ? '#f59e0b' : '#ef4444'} />
              <div>
                <strong>You are about to {roleUser.role === 'admin' ? 'PROMOTE' : 'DEMOTE'} this user</strong>
                <p>
                  {roleUser.role === 'admin' 
                    ? 'They will gain FULL SuperAdmin access to manage ALL users, including other SuperAdmins.'
                    : 'They will LOSE SuperAdmin privileges and become a regular Admin with limited access.'
                  }
                </p>
              </div>
            </div>
          </div>
          
          <div style={styles.modalFooter}>
            <button 
              onClick={confirmRoleChange} 
              style={{ ...styles.primaryBtn, background: roleUser.role === 'admin' ? '#f59e0b' : '#8b5cf6' }}
            >
              {roleUser.role === 'admin' ? '👑 Promote to SUPERADMIN' : '🛡️ Demote to ADMIN'}
            </button>
            <button onClick={() => setShowRoleModal(false)} style={styles.secondaryBtn}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* Trial Modal */}
      {showTrialModal && trialUser && (
        <Modal onClose={() => setShowTrialModal(false)}>
          <div style={styles.modalHeader}>
            <Calendar size={24} color="#8b5cf6" />
            <h3>Set Trial Period</h3>
          </div>
          
          <div style={styles.modalBody}>
            <div style={styles.infoBox}>
              <p><strong>User:</strong> {trialUser.name}</p>
              <p><strong>Email:</strong> {trialUser.email}</p>
              <p><strong>Current Status:</strong> 
                <span style={{ ...styles.badge, marginLeft: '8px', background: getStatusBadge(trialUser.status).bg, color: getStatusBadge(trialUser.status).color }}>
                  {trialUser.status.toUpperCase()}
                </span>
              </p>
              {trialUser.trial?.endDate && (
                <p><strong>Current Trial End:</strong> {formatDateTime(trialUser.trial.endDate)}</p>
              )}
            </div>
            
            <div style={styles.formGroup}>
              <label>New Trial End Date</label>
              <input
                type="date"
                value={trialDate}
                onChange={e => setTrialDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                style={styles.input}
              />
            </div>
          </div>
          
          <div style={styles.modalFooter}>
            <button onClick={saveTrial} style={{ ...styles.primaryBtn, background: '#8b5cf6' }}>💾 Save Date</button>
            <button onClick={resetTrial} style={{ ...styles.primaryBtn, background: '#10b981' }}>🔄 Reset to 7 Days</button>
            <button onClick={() => setShowTrialModal(false)} style={styles.secondaryBtn}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* Transfer Modal */}
      {showTransferModal && transferStaff && (
        <Modal onClose={() => setShowTransferModal(false)}>
          <div style={styles.modalHeader}>
            <ArrowRightLeft size={24} color="#f59e0b" />
            <h3>Transfer Staff</h3>
          </div>
          
          <div style={styles.modalBody}>
            <div style={styles.infoBox}>
              <p><strong>Staff:</strong> {transferStaff.name}</p>
              <p><strong>Email:</strong> {transferStaff.email}</p>
              <p><strong>Staff ID:</strong> {transferStaff.staffId}</p>
            </div>
            
            <div style={styles.formGroup}>
              <label>Select Target Admin</label>
              <select
                value={transferToAdmin}
                onChange={e => setTransferToAdmin(e.target.value)}
                style={styles.input}
              >
                <option value="">-- Select Admin --</option>
                {adminsData
                  .filter(a => a._id !== transferStaff.createdBy)
                  .map(a => (
                    <option key={a._id} value={a._id}>
                      {a.name} - {a.pharmacyName}
                    </option>
                  ))
                }
              </select>
            </div>
          </div>
          
          <div style={styles.modalFooter}>
            <button 
              onClick={confirmTransfer} 
              disabled={!transferToAdmin}
              style={{ ...styles.primaryBtn, background: '#f59e0b', opacity: transferToAdmin ? 1 : 0.5 }}
            >
              ✅ Confirm Transfer
            </button>
            <button onClick={() => setShowTransferModal(false)} style={styles.secondaryBtn}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && passwordUser && (
        <Modal onClose={() => setShowPasswordModal(false)}>
          <div style={styles.modalHeader}>
            <Key size={24} color="#8b5cf6" />
            <h3>Reset Password</h3>
          </div>
          
          <div style={styles.modalBody}>
            <div style={styles.infoBox}>
              <p><strong>Staff:</strong> {passwordUser.name}</p>
              <p><strong>Email:</strong> {passwordUser.email}</p>
            </div>
            
            <div style={styles.formGroup}>
              <label>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                style={styles.input}
              />
            </div>
          </div>
          
          <div style={styles.modalFooter}>
            <button 
              onClick={confirmResetPassword}
              disabled={!newPassword || newPassword.length < 6}
              style={{ ...styles.primaryBtn, background: '#8b5cf6', opacity: newPassword?.length >= 6 ? 1 : 0.5 }}
            >
              🔐 Reset Password
            </button>
            <button onClick={() => setShowPasswordModal(false)} style={styles.secondaryBtn}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* View/Edit/Delete Modal */}
      {showModal && selectedUser && (
        <Modal onClose={() => setShowModal(false)} wide>
          <div style={styles.modalHeader}>
            {modalMode === 'view' && <Eye size={24} color="#6366f1" />}
            {modalMode === 'edit' && <Edit2 size={24} color="#10b981" />}
            {modalMode === 'delete' && <Trash2 size={24} color="#ef4444" />}
            <h3>
              {modalMode === 'view' && `${selectedUserType === 'superadmin' ? 'Super Admin' : selectedUserType === 'admin' ? 'Admin' : 'Staff'} Details`}
              {modalMode === 'edit' && `Edit ${selectedUserType === 'superadmin' ? 'Super Admin' : selectedUserType === 'admin' ? 'Admin' : 'Staff'}`}
              {modalMode === 'delete' && `Delete ${selectedUserType === 'superadmin' ? 'Super Admin' : selectedUserType === 'admin' ? 'Admin' : 'Staff'}`}
            </h3>
          </div>
          
          <div style={styles.modalBody}>
            {modalMode === 'delete' ? (
              <>
                <div style={{ ...styles.warningBox, background: '#fef2f2', borderColor: '#fecaca' }}>
                  <AlertTriangle size={24} color="#dc2626" />
                  <div>
                    <strong>⚠️ Warning: This action cannot be undone!</strong>
                    <p>You are about to permanently delete this user and all their data.</p>
                  </div>
                </div>
                
                <div style={styles.infoBox}>
                  <p><strong>Name:</strong> {selectedUser.name}</p>
                  <p><strong>Email:</strong> {selectedUser.email}</p>
                  <p><strong>Role:</strong> 
                    <span style={{ ...styles.badge, marginLeft: '8px', background: getRoleBadge(selectedUser.role || selectedUserType).bg, color: getRoleBadge(selectedUser.role || selectedUserType).color }}>
                      {(selectedUser.role || selectedUserType).toUpperCase()}
                    </span>
                  </p>
                  {(selectedUserType === 'admin' || selectedUserType === 'superadmin') && <p><strong>Pharmacy:</strong> {selectedUser.pharmacyName || 'N/A'}</p>}
                </div>
              </>
            ) : (
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={selectedUser.name || ''}
                    onChange={e => setSelectedUser({ ...selectedUser, name: e.target.value })}
                    disabled={modalMode === 'view'}
                    style={styles.input}
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label>Email</label>
                  <input
                    type="email"
                    value={selectedUser.email || ''}
                    onChange={e => setSelectedUser({ ...selectedUser, email: e.target.value })}
                    disabled={modalMode === 'view'}
                    style={styles.input}
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label>Phone</label>
                  <input
                    type="text"
                    value={selectedUser.phone || ''}
                    onChange={e => setSelectedUser({ ...selectedUser, phone: e.target.value })}
                    disabled={modalMode === 'view'}
                    style={styles.input}
                  />
                </div>
                
                {(selectedUserType === 'admin' || selectedUserType === 'superadmin') && (
                  <>
                    <div style={styles.formGroup}>
                      <label>Pharmacy Name</label>
                      <input
                        type="text"
                        value={selectedUser.pharmacyName || ''}
                        onChange={e => setSelectedUser({ ...selectedUser, pharmacyName: e.target.value })}
                        disabled={modalMode === 'view'}
                        style={styles.input}
                      />
                    </div>
                    
                    <div style={styles.formGroup}>
                      <label>PAN Number</label>
                      <input
                        type="text"
                        value={selectedUser.pan || ''}
                        onChange={e => setSelectedUser({ ...selectedUser, pan: e.target.value.toUpperCase() })}
                        disabled={modalMode === 'view'}
                        style={styles.input}
                      />
                    </div>
                  </>
                )}
                
                {modalMode === 'view' && (
                  <div style={styles.formGroup}>
                    <label>Role</label>
                    <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                      <span style={{ ...styles.badge, background: getRoleBadge(selectedUser.role || selectedUserType).bg, color: getRoleBadge(selectedUser.role || selectedUserType).color }}>
                        {getRoleBadge(selectedUser.role || selectedUserType).icon} {(selectedUser.role || selectedUserType).toUpperCase()}
                      </span>
                    </div>
                  </div>
                )}
                
                {(selectedUserType === 'admin' || selectedUserType === 'superadmin') && modalMode === 'edit' && (
                  <div style={styles.formGroup}>
                    <label>Status</label>
                    <select
                      value={selectedUser.status || 'trial'}
                      onChange={e => setSelectedUser({ ...selectedUser, status: e.target.value })}
                      style={styles.input}
                    >
                      <option value="active">✅ Active</option>
                      <option value="trial">🎁 Trial</option>
                      <option value="expired">⏰ Expired</option>
                      <option value="inactive">⏸️ Inactive</option>
                      <option value="blocked">🚫 Blocked</option>
                    </select>
                  </div>
                )}
                
                {selectedUserType === 'staff' && (
                  <div style={styles.formGroup}>
                    <label>Staff ID</label>
                    <input
                      type="text"
                      value={selectedUser.staffId || ''}
                      disabled
                      style={{ ...styles.input, background: '#f1f5f9' }}
                    />
                  </div>
                )}
                
                {selectedUserType === 'staff' && modalMode === 'view' && (
                  <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                    <label>Permissions</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                      {selectedUser.permissions?.length > 0 ? (
                        selectedUser.permissions.map(perm => (
                          <span key={perm} style={{ ...styles.badge, background: '#d1fae5', color: '#065f46' }}>
                            <CheckCircle size={10} /> {perm}
                          </span>
                        ))
                      ) : (
                        <span style={{ color: '#94a3b8' }}>No permissions assigned</span>
                      )}
                    </div>
                  </div>
                )}
                
                {modalMode === 'view' && (
                  <>
                    <div style={styles.formGroup}>
                      <label>Created At</label>
                      <input
                        type="text"
                        value={formatDateTime(selectedUser.createdAt)}
                        disabled
                        style={{ ...styles.input, background: '#f1f5f9' }}
                      />
                    </div>
                    
                    <div style={styles.formGroup}>
                      <label>Last Updated</label>
                      <input
                        type="text"
                        value={formatDateTime(selectedUser.updatedAt)}
                        disabled
                        style={{ ...styles.input, background: '#f1f5f9' }}
                      />
                    </div>
                  </>
                )}
                
                {(selectedUserType === 'admin' || selectedUserType === 'superadmin') && modalMode === 'view' && selectedUser.trial && (
                  <>
                    <div style={styles.formGroup}>
                      <label>Trial Start</label>
                      <input
                        type="text"
                        value={formatDateTime(selectedUser.trial.startDate)}
                        disabled
                        style={{ ...styles.input, background: '#f1f5f9' }}
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label>Trial End</label>
                      <input
                        type="text"
                        value={formatDateTime(selectedUser.trial.endDate)}
                        disabled
                        style={{ ...styles.input, background: '#f1f5f9' }}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          
          <div style={styles.modalFooter}>
            {modalMode === 'delete' && (
              <button onClick={confirmDelete} style={{ ...styles.primaryBtn, background: '#ef4444' }}>
                🗑️ Yes, Delete Permanently
              </button>
            )}
            {modalMode === 'edit' && (
              <button onClick={saveEdit} style={{ ...styles.primaryBtn, background: '#10b981' }}>
                💾 Save Changes
              </button>
            )}
            <button onClick={() => setShowModal(false)} style={styles.secondaryBtn}>
              {modalMode === 'view' ? 'Close' : 'Cancel'}
            </button>
          </div>
        </Modal>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes slideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }
        @keyframes progressShrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}

// ==================== SUB COMPONENTS ====================

const StatCard = ({ title, value, color, icon }) => (
  <div style={{ 
    background: 'white', 
    padding: '20px', 
    borderRadius: '12px', 
    borderLeft: `4px solid ${color}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }}>
    <div>
      <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '4px' }}>{title}</div>
      <div style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b' }}>{value}</div>
    </div>
    <div style={{ color, opacity: 0.7 }}>{icon}</div>
  </div>
);

const ActionBtn = ({ icon, color, onClick, disabled, title }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={{
      background: disabled ? '#f1f5f9' : `${color}15`,
      color: disabled ? '#cbd5e1' : color,
      border: 'none',
      padding: '8px',
      borderRadius: '8px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s',
      opacity: disabled ? 0.5 : 1
    }}
  >
    {icon}
  </button>
);

const Modal = ({ children, onClose, wide }) => (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px'
  }}>
    <div style={{
      background: 'white',
      padding: '0',
      borderRadius: '16px',
      width: wide ? '650px' : '480px',
      maxWidth: '95%',
      maxHeight: '90vh',
      overflow: 'hidden',
      boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {children}
    </div>
  </div>
);

// ==================== STYLES ====================
const styles = {
  container: {
    padding: '30px',
    fontFamily: "'Poppins', 'Inter', sans-serif",
    background: '#f8fafc',
    minHeight: '100vh'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  title: {
    fontSize: '26px',
    fontWeight: '700',
    color: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: 0
  },
  subtitle: {
    color: '#64748b',
    marginTop: '8px',
    fontSize: '14px'
  },
  exportBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    background: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '16px',
    marginBottom: '24px'
  },
  controls: {
    background: 'white',
    padding: '16px',
    borderRadius: '12px',
    marginBottom: '24px',
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    alignItems: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
  },
  searchWrapper: {
    position: 'relative',
    flex: '1',
    minWidth: '250px'
  },
  searchIcon: {
    position: 'absolute',
    top: '12px',
    left: '12px',
    color: '#94a3b8'
  },
  searchInput: {
    width: '100%',
    padding: '10px 10px 10px 40px',
    border: '2px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box'
  },
  select: {
    padding: '10px 16px',
    border: '2px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '14px',
    outline: 'none',
    background: 'white',
    cursor: 'pointer'
  },
  controlBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    background: '#f1f5f9',
    color: '#475569',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  
  // Section Styles
  sectionContainer: {
    background: 'white',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    marginBottom: '24px'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
    borderBottom: '1px solid #fde68a',
    cursor: 'pointer'
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontWeight: '700',
    color: '#92400e',
    fontSize: '16px'
  },
  sectionIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    background: '#fef3c7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  sectionToggle: {
    color: '#92400e'
  },
  sectionBody: {
    padding: '0'
  },
  
  // SuperAdmin Row
  superAdminRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #f1f5f9',
    gap: '16px',
    background: 'white'
  },
  superAdminAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    flexShrink: 0
  },
  
  currentUserBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 20px',
    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
    borderRadius: '10px',
    marginBottom: '24px',
    color: '#1e40af',
    fontSize: '14px'
  },

  userList: {
    background: 'white',
    borderRadius: '0',
    overflow: 'hidden'
  },
  loadingState: {
    padding: '60px',
    textAlign: 'center',
    color: '#64748b',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e2e8f0',
    borderTopColor: '#6366f1',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  emptyState: {
    padding: '60px',
    textAlign: 'center',
    color: '#94a3b8',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px'
  },
  adminCard: {
    borderBottom: '1px solid #f1f5f9'
  },
  adminRow: {
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    transition: 'background 0.2s'
  },
  expandBtn: {
    border: 'none',
    background: 'transparent',
    padding: '6px',
    cursor: 'pointer',
    borderRadius: '6px'
  },
  avatar: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontWeight: '700',
    fontSize: '18px',
    flexShrink: 0
  },
  userInfo: {
    flex: 1,
    minWidth: 0
  },
  userName: {
    fontWeight: '700',
    color: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    marginBottom: '4px'
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '10px',
    fontWeight: '600',
    padding: '3px 8px',
    borderRadius: '6px'
  },
  userMeta: {
    display: 'flex',
    gap: '16px',
    fontSize: '12px',
    color: '#64748b',
    flexWrap: 'wrap'
  },
  staffCount: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: '600',
    padding: '6px 12px',
    background: '#f1f5f9',
    borderRadius: '8px'
  },
  actions: {
    display: 'flex',
    gap: '6px',
    flexShrink: 0
  },
  staffContainer: {
    background: '#f8fafc'
  },
  staffRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '14px 20px 14px 50px',
    borderTop: '1px solid #e2e8f0',
    gap: '16px'
  },
  staffConnector: {
    width: '24px',
    height: '2px',
    background: '#cbd5e1',
    marginLeft: '-10px'
  },
  staffAvatar: {
    width: '38px',
    height: '38px',
    borderRadius: '10px',
    background: '#e0f2fe',
    color: '#0369a1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '15px',
    flexShrink: 0
  },
  staffInfo: {
    flex: 1,
    minWidth: 0
  },
  staffName: {
    fontWeight: '600',
    color: '#334155',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    marginBottom: '2px',
    fontSize: '14px'
  },
  staffMeta: {
    display: 'flex',
    gap: '14px',
    fontSize: '12px',
    color: '#64748b',
    flexWrap: 'wrap'
  },
  noStaff: {
    padding: '20px 50px',
    background: '#f8fafc',
    color: '#94a3b8',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderTop: '1px solid #e2e8f0'
  },
  summary: {
    textAlign: 'center',
    padding: '16px',
    color: '#64748b',
    fontSize: '14px'
  },
  
  // Modal Styles
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '20px 24px',
    borderBottom: '1px solid #e2e8f0',
    background: '#f8fafc'
  },
  modalBody: {
    padding: '24px',
    overflowY: 'auto',
    maxHeight: '60vh'
  },
  modalFooter: {
    display: 'flex',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid #e2e8f0',
    background: '#f8fafc'
  },
  infoBox: {
    background: '#f1f5f9',
    padding: '16px',
    borderRadius: '10px',
    marginBottom: '16px',
    fontSize: '14px',
    lineHeight: '1.8'
  },
  warningBox: {
    display: 'flex',
    gap: '12px',
    background: '#fffbeb',
    border: '1px solid #fef3c7',
    padding: '16px',
    borderRadius: '10px',
    marginBottom: '16px',
    color: '#b45309',
    fontSize: '14px'
  },
  formGroup: {
    marginBottom: '16px'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px'
  },
  input: {
    width: '100%',
    padding: '12px',
    border: '2px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    marginTop: '6px'
  },
  primaryBtn: {
    flex: 1,
    padding: '12px 20px',
    background: '#6366f1',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  secondaryBtn: {
    flex: 1,
    padding: '12px 20px',
    background: '#e2e8f0',
    color: '#475569',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  }
};